<template>
  <div v-on-click-outside="dismissSearch" class="gam-input-search">
    <GamInput
      ref="searchInput"
      v-bind="getSearchInput"
      v-model="query"
      :on-click-clear="handleClear"
      @clear="clearInput"
      @close="closeDropdown"
      @click-message="onClickMessage"
    />

    <GamSimpleDropdown
      v-if="getDropdown && isDropdownOpen && items.length"
      v-bind="getDropdown"
      @update="updateSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { MapLocationSource } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { GamDropdownCategory } from '@/views/composables/constants/components/gamDropdown.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import type { GamDropdownItem, GamSimpleDropdownType } from '@/views/composables/models/components/GamDropdown';
import type { GamInputType, GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import { vOnClickOutside } from '@vueuse/components';
import { debounce } from 'lodash';
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import GamInput from './GamInput.vue';
import GamSimpleDropdown from './GamSimpleDropdown.vue';

const props = defineProps<GamSimpleInputSearchType<GamDropdownItem>>();

const query = ref<string>(props.input.value?.toString() || '');
const items = ref<GamDropdownItem[]>([]);
const infiniteLoad = () => {};

const searchInput = ref();
const loadedOnInit = ref<boolean>(false);
const selectedItem = ref<GamDropdownItem | null>(null);
const isLoaded = ref<boolean>(false);

onBeforeMount(async () => {
  setDefaultItem();

  if (props.loadCallback) {
    if (props.loadOnInit) {
      await initLoad();
    } else {
      loadedOnInit.value = true;
    }
  }
});

watch(
  () => props.input.value,
  (value) => {
    const newValue = value?.toString() || '';
    if (newValue !== query.value) {
      query.value = newValue;
    }
  },
);

const initLoad = async () => {
  loadedOnInit.value = true;
};

const isSearchEntered = (): boolean => {
  const inputValue = query.value?.toString() || '';
  return inputValue.length > 0 && !isDefaultValue();
};

const setDefaultItem = (): void => {
  if (props.dropdown?.selected) {
    selectedItem.value = props.dropdown.selected;
  } else if (props.dropdown?.defaultItem) {
    selectedItem.value = props.dropdown.defaultItem;
  }
};

const isDefaultSelected = (): boolean => {
  return !!props.dropdown?.defaultItem && selectedItem.value?.id === props.dropdown?.defaultItem?.id;
};

const isDefaultValue = (): boolean => {
  return query.value === localize(props.dropdown?.defaultItem?.label || '');
};

const closeDropdown = () => {
  if (searchInput.value && props.dropdown) {
    searchInput.value.isActive = false;
  }
};

const dismissSearch = () => {
  const committed = props.input.value?.toString() || '';
  if (query.value !== committed) {
    query.value = committed;
  }
  closeDropdown();
};

const clearInput = () => {
  if (props.input.canClear) {
    query.value = '';
    selectedItem.value = null;
  }
};

const handleClear = () => {
  props.onClickClear?.();
  searchInput.value?.focus?.();
};

const handleFocus = () => {
  props.onFocus?.();
  if (query.value && loadedOnInit.value) {
    loadItems({ query: query.value, selectFirstItem: false });
  }
};

const isDropdownOpen = computed((): boolean => {
  const isInputActive = searchInput.value?.isActive;
  return !!props.dropdown && ((isInputActive && isSearchEntered()) || (props.loadOnInit && isInputActive));
});

const getSearchInput = computed((): GamInputType => {
  return {
    ...props.input,
    name: GamInputName.SEARCH,
    value: props.input.value || query.value,
    isSearch: props.isSearch,
    isDropdownOpen: props.dropdown && isDropdownOpen.value && items.value.length > 0,
    flagIcon: selectedItem.value?.flagIcon,
    onMounted: props.onMounted,
    onFocus: handleFocus,
    onBlur: props.onBlur,
  };
});

const getDropdown = computed((): GamSimpleDropdownType | undefined => {
  if (!props.dropdown) return;
  return {
    ...props.dropdown,
    isReversed: props.dropdown.reversed,
    query: query.value,
    items: items.value,
    infiniteLoad,
    isOpen: isDropdownOpen.value,
    maxItems: props.dropdown?.maxItems ?? 5,
  };
});

const updateSelected = async (result?: GamDropdownItem | null) => {
  if (!result) return;

  if (result.source !== MapLocationSource.GOOGLE) {
    query.value = result.label;
    selectedItem.value = result;
    closeDropdown();

    return;
  }

  const { data: place } =
    (await gambits.locationService.fetchGooglePlaceDetails({
      placeId: result.id!,
      description: result.label,
      mainText: result.label,
      secondaryText: result.subLabel!,
      sessionToken: result.sessionToken!,
    })) || {};

  if (!place) {
    query.value = result.label;
    selectedItem.value = result;
    closeDropdown();

    return;
  }

  selectedItem.value = {
    ...result,
    ...place,
  };
  query.value = result.label;
  closeDropdown();
};

// TODO: This gets called when blurring a cleared input
watch(selectedItem, async (selected: GamDropdownItem | null) => {
  if (props.selectCallback) {
    props.selectCallback(selected);
  }
});

watch(isDropdownOpen, async (isOpen: boolean) => {
  if (props.dropdown) {
    if (isOpen) {
      await nextTick(async () => {
        if (props.loadOnInit && !loadedOnInit.value) {
          await initLoad();
        } else {
          isLoaded.value = true;
          loadedOnInit.value = true;
        }
      });
    } else if (loadedOnInit.value) {
      if (selectedItem.value) {
        if (isDefaultSelected()) {
          const newQuery = localize(props.dropdown?.defaultItem?.label || '');
          query.value = newQuery;
        } else if (props.dropdown?.category === GamDropdownCategory.DEFAULT) {
          query.value = '';
        }
      }
    }
  }
});

type LoadItemsOptions = {
  query: string;
  selectFirstItem: boolean;
};

async function loadItems({ query }: LoadItemsOptions) {
  const results = await props.loadCallback?.(query);

  if (!results?.data) {
    items.value = [];

    return;
  }

  items.value = results.data;
}

watch(
  () => query.value,
  debounce(async (query, prevQuery) => {
    if (!searchInput.value?.isActive) return;
    if (query === prevQuery || query === selectedItem.value?.label) return;

    if (isSearchEntered() && loadedOnInit.value) {
      props.onQueryChanged && props.onQueryChanged(query);

      loadItems({ query, selectFirstItem: LocationHelper.isValidDDFormat(query) });
    } else if (!isSearchEntered()) {
      props.onQueryChanged && props.onQueryChanged(undefined);
    }
  }, 400),
);

watch(
  () => searchInput.value?.isActive,
  (value) => {
    if (!value) {
      setDefaultItem();
      if (!selectedItem.value && props.input.value) {
        query.value = props.input.value.toString();
      }
    }
  },
);
</script>

<style scoped lang="scss">
.gam-input-search {
  display: flex;
  position: relative;
  align-items: flex-start;
  align-self: stretch;
  flex: 1 0 0;
}

.gam-simple-input {
  width: 100%;
}
</style>
